import { DashboardSnapshot } from '../models/dashboard.model';
import { plural } from './dashboard-flow';

// Primeiros passos (spec §12): empresa sem nenhum pedido em todo o histórico troca os widgets por
// três passos (catálogo, canal, anúncios), feitos ou pendentes conforme os dados do snapshot.

export type FirstStepId = 'catalog' | 'channel' | 'ads';

export interface FirstStep {
  id: FirstStepId;
  title: string;
  text: string;
  done: boolean;
  /** Canal: há canais, mas nenhum com status conhecido (usuário sem `integration`). Sem marca de feito. */
  unknown: boolean;
}

/**
 * A empresa nunca recebeu pedido. Só vale quando a API manda `firstActivityAt` (null = nenhum pedido;
 * ausente = API antiga, home normal). Com canal escolhido, o campo do snapshot é daquele canal: então
 * só conta como "sem vendas" se também nenhum canal da empresa tem pedido.
 */
export function hasNoOrders(snapshot: DashboardSnapshot | undefined): boolean {
  if (!snapshot || snapshot.firstActivityAt !== null) return false;
  return (snapshot.channels ?? []).every((c) => !c.firstActivityAt);
}

export function firstSteps(snapshot: DashboardSnapshot): FirstStep[] {
  const products = snapshot.catalog?.products ?? 0;
  const published = snapshot.catalog?.adsPublished ?? 0;
  const channels = snapshot.channels ?? [];
  const activeChannels = channels.filter((c) => c.status === 'Active').length;
  const channelDone = activeChannels > 0;
  const channelUnknown = !channelDone && channels.some((c) => c.status === null);
  const example = snapshot.groupChannelExample?.trim();

  return [
    {
      id: 'catalog',
      title: 'Catálogo',
      done: products > 0,
      unknown: false,
      text:
        products > 0
          ? `${products.toLocaleString('pt-BR')} ${plural(products, 'produto do grupo já está', 'produtos do grupo já estão')} aqui.`
          : 'Cadastre ou importe os produtos que vão para os canais.',
    },
    {
      id: 'channel',
      title: 'Conectar um canal de venda',
      done: channelDone,
      unknown: channelUnknown,
      text: channelDone
        ? `${activeChannels.toLocaleString('pt-BR')} ${plural(activeChannels, 'canal ativo', 'canais ativos')}.`
        : example
          ? `${example}, do mesmo grupo, já usa um canal.`
          : 'Conecte o marketplace onde você vai vender.',
    },
    {
      id: 'ads',
      title: 'Publicar os anúncios',
      done: published > 0,
      unknown: false,
      text:
        published > 0
          ? `${published.toLocaleString('pt-BR')} ${plural(published, 'anúncio publicado', 'anúncios publicados')}.`
          : 'Publique os produtos nos canais conectados.',
    },
  ];
}
